/**
 * WorkerPool per-driver CPU/PSS calibration sampler.
 *
 * Answers "how much CPU and memory does a child of this driver actually use?" —
 * the input to sizing `N` browser slots, the expensive-pool size, and the
 * per-driver RSS caps from real data rather than assumption. It attributes CPU
 * (and, for leased sessions, PSS) per driver and reports p50/p95/max, split by
 * checkout model:
 *
 * - **Per-call** (cpu-offload, ffmpeg): the pool takes a `/proc/<pid>/stat` CPU
 *   snapshot at checkout and again at settle; `vCPU = ΔcpuSeconds / ΔwallSeconds`
 *   over the exact call window. CPU ticks sum utime+stime **plus cutime+cstime**
 *   (the reaped-children fields), so a driver whose real work runs in a
 *   short-lived grandchild that exits before settle — the ffmpeg encode — still
 *   has that CPU folded in once the runner reaps it. (This assumes the driver
 *   **reaps its grandchildren before the call settles** — true for today's
 *   per-call runners; a driver that returns while a grandchild is still live would
 *   under-count, since the per-call path reads only the child pid, not the group.)
 *   A poll would sample these transient children mostly idle and understate their
 *   peak, which is why per-call work is measured over its own window, not sampled.
 * - **Poll** (leased browsers): a periodic pass reads each *leased* child, summing
 *   CPU + PSS **over its process group** — a `killProcessGroup` runner's real
 *   Chromium lives in still-running grandchildren sharing its pgid, so measuring
 *   the runner pid alone would miss almost all of the cost. Memory is summed as
 *   **PSS** (`smaps_rollup`), not VmRSS: a Chromium tree shares its text/zygote
 *   pages across ~10 processes, so a VmRSS sum counts them once per process
 *   (~3× the true footprint — dev measured ~1.6 GB VmRSS-sum vs ~550 MB PSS for
 *   one leased browser). PSS is the honest per-slot number for sizing `N`; the
 *   per-child RSS *cap* is calibrated by `rssHighWaterBytes` instead, which uses
 *   VmRSS like the enforcing monitor.
 *
 * Per-call records **vCPU only**: memory for the per-call drivers is already
 * covered by the pool's `rssHighWaterBytes` (cpu-offload's RSS monitor / the
 * ffmpeg runner's reported peak), and an exited grandchild's memory is unreadable
 * by the parent anyway. The memory distribution comes from the poll path, where
 * the browser is long-lived and group-summable.
 *
 * All reads are `/proc`-based from the parent (Linux-only); off-Linux every read
 * returns `undefined` and the sampler records nothing — a no-op on macOS/dev, like
 * the RSS monitor. The whole instrument is inert unless the pool is built with
 * `measureResourcesMs`, so an un-calibrated pool is byte-identical to today.
 *
 * Cost note: `smaps_rollup` makes the kernel walk the process's VMAs — the same
 * read the 60 s worker-memory telemetry deliberately throttles to once a minute,
 * here run per leased-group member at the poll cadence (500 ms floor). That is
 * acceptable only because it is scoped to leased-group pids and the sampler is a
 * turn-on-to-calibrate-then-off tool, not steady-state. At max leased load
 * (many browsers × ~10–25 pids each) a pass's serialized reads can approach the
 * interval; the `sampling` guard then skips ticks rather than piling up, which
 * lowers the effective cadence — compare `ExpensivePoolResourceSampleCount`
 * against the expected `window / cadence` to detect it.
 *
 * @module worker-pool/resource-sampler
 */

import { readdir, readFile } from 'node:fs/promises';
import type { BrowserLogger } from '../logger.js';
import type { PooledChild } from './bookkeeping.js';
import { readProcPssBytes, readProcRssBytes } from './rss-monitor.js';

/**
 * Kernel clock ticks per second (`sysconf(_SC_CLK_TCK)`). Node exposes no
 * `sysconf`, but this is **100** on every Linux we run on (Fargate included), so
 * `/proc/<pid>/stat` CPU tick counts divide by 100 to get CPU-seconds. A
 * calibration signal tolerates the rare non-100 kernel; a native addon is not
 * worth it.
 */
const USER_HZ = 100;

/** Cap on retained samples per dimension — a rolling window, newest kept. */
const MAX_SAMPLES = 256;

/** Highest sane vCPU a single reading can post; guards a bogus tick delta (e.g. a
 *  reused pid) from poisoning the max. 128 cores is well above any worker tier. */
const MAX_PLAUSIBLE_VCPU = 128;

/**
 * Shortest per-call window (ms) that yields a meaningful vCPU ratio. CPU time is
 * charged in whole {@link USER_HZ} ticks (10 ms), so a call whose wall-clock is
 * shorter than a few ticks quantizes into garbage — one tick charged to a 2 ms
 * call reads as "5 vCPU" (dev measured cpu-offload "p95 10 / max 15" this way,
 * physically impossible for a single-threaded child). Calls below the floor are
 * not recorded: at ≥100 ms a ±1-tick error is ±0.1 vCPU, and sub-100 ms calls
 * are irrelevant to convergence sizing — their absolute CPU cost is a few ticks
 * at most, which cannot wedge a pool slot.
 */
export const MIN_MEASURABLE_CALL_WALL_MS = 100;

/**
 * Consecutive poll passes with leased children but zero readable group data, after
 * which we warn once — the sibling `WorkerPoolRssMonitor`'s false-green guard, so an
 * operator who just armed the knob to calibrate learns the `/proc` reads are silently
 * failing (a broken host) rather than mistaking it for "no samples yet".
 */
const READ_FAILURE_WARN_STREAK = 5;

export interface ResourcePercentiles {
  p50: number;
  p95: number;
  max: number;
}

/**
 * Per-driver CPU (vCPU ratio) and memory (PSS bytes, group-summed) distributions
 * over the window. A dimension is present only once it has ≥1 sample — per-call
 * drivers report `vcpu` only, leased-session drivers report both.
 */
export interface WorkerPoolResourceStats {
  vcpu?: ResourcePercentiles;
  /** Group-summed PSS (proportional set size) of a leased child's process tree —
   *  the true incremental footprint, unlike a VmRSS sum which multi-counts pages
   *  shared across the tree. Falls back to VmRSS per-pid where `smaps_rollup` is
   *  unreadable, so a reading is at worst an upper bound. */
  pssBytes?: ResourcePercentiles;
  /** Total samples recorded (per-call settles + poll passes) since start. */
  sampleCount: number;
}

/** The `/proc/<pid>/stat` fields the sampler needs. */
export interface ProcStat {
  /** Process-group id (stat field 5) — used to group a runner with its browser. */
  pgrp: number;
  /** utime+stime+cutime+cstime (stat fields 14–17), in {@link USER_HZ} ticks. */
  cpuTicks: number;
}

/** Nearest-rank value at quantile `q` (0–1) of an already-ascending array. */
export function rankSorted(sorted: number[], q: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(q * sorted.length) - 1));
  return sorted[idx] ?? 0;
}

/**
 * Nearest-rank percentile of `nums` at quantile `q` (0–1). Returns 0 for an empty
 * set so an un-sampled dimension reads as 0 rather than NaN.
 */
export function percentile(nums: number[], q: number): number {
  return rankSorted([...nums].sort((a, b) => a - b), q);
}

/** p50/p95/max of `nums`; all 0 when empty. Sorts once and derives all three. */
export function percentiles(nums: number[]): ResourcePercentiles {
  if (nums.length === 0) return { p50: 0, p95: 0, max: 0 };
  const sorted = [...nums].sort((a, b) => a - b);
  return { p50: rankSorted(sorted, 0.5), p95: rankSorted(sorted, 0.95), max: sorted[sorted.length - 1] };
}

/**
 * Parse `/proc/<pid>/stat` for pgrp + total CPU ticks. `cpuTicks` sums utime,
 * stime, cutime, cstime so that CPU spent by reaped children (an exited ffmpeg
 * encode) is included. Tolerates spaces/parens in the comm field.
 */
export function parseProcStat(text: string): ProcStat | undefined {
  // comm (field 2) is parenthesised and may contain spaces/parens, so parse the
  // fixed-width fields *after* the last ')'. Field 3 (state) is then index 0.
  const close = text.lastIndexOf(')');
  if (close < 0) return undefined;
  const rest = text.slice(close + 2).trim().split(/\s+/);
  // rest[2]=pgrp(5)  rest[11..14]=utime(14) stime(15) cutime(16) cstime(17)
  const pgrp = Number(rest[2]);
  const ticks = [rest[11], rest[12], rest[13], rest[14]].map(Number);
  if (!Number.isFinite(pgrp) || ticks.some((t) => !Number.isFinite(t))) return undefined;
  return { pgrp, cpuTicks: ticks[0] + ticks[1] + ticks[2] + ticks[3] };
}

async function defaultReadProcStat(pid: number): Promise<ProcStat | undefined> {
  let text: string;
  try {
    text = await readFile(`/proc/${pid}/stat`, 'utf8');
  } catch {
    return undefined;
  }
  return parseProcStat(text);
}

/**
 * Group-member memory read: PSS when `smaps_rollup` is readable, else VmRSS.
 * The fallback keeps a pid *counted* on kernels/mounts without `smaps_rollup`
 * (better an upper bound than a silent zero), at the cost of mixing units for
 * that pid — acceptable for a calibration signal.
 */
async function defaultReadPssBytes(pid: number): Promise<number | undefined> {
  return (await readProcPssBytes(pid)) ?? (await readProcRssBytes(pid));
}

async function defaultListProcPids(): Promise<number[]> {
  try {
    const entries = await readdir('/proc');
    return entries.filter((e) => /^\d+$/.test(e)).map(Number);
  } catch {
    return [];
  }
}

/** A per-call CPU/wall snapshot taken at checkout, redeemed at settle. */
export interface CallSample {
  startTicks: number;
  startAt: number;
}

export interface WorkerPoolResourceSamplerOptions<Handle> {
  /** The pool's live child set — poll reads the *leased* subset. */
  children: Set<PooledChild<Handle>>;
  /** Poll cadence (ms) for leased sessions; also the arm switch (>0 ⇒ enabled). */
  intervalMs: number;
  now: () => number;
  isDraining: () => boolean;
  logger: BrowserLogger;
  /** Injectable readers (tests). Default to the `/proc` implementations. */
  readProcStat?: (pid: number) => Promise<ProcStat | undefined>;
  /** Per-pid memory for group members — defaults to PSS with a VmRSS fallback. */
  readPssBytes?: (pid: number) => Promise<number | undefined>;
  listProcPids?: () => Promise<number[]>;
  /** Injectable platform gate for the false-green WARN (tests). Defaults to Linux. */
  isLinux?: () => boolean;
}

/**
 * Accumulates per-driver CPU/PSS samples for one pool and reports percentiles.
 * Owns the leased-session poll loop; the pool feeds it per-call samples via
 * {@link beginCall}/{@link endCall}.
 */
export class WorkerPoolResourceSampler<Handle> {
  private readonly children: Set<PooledChild<Handle>>;
  private readonly intervalMs: number;
  private readonly now: () => number;
  private readonly isDraining: () => boolean;
  private readonly logger: BrowserLogger;
  private readonly readProcStat: (pid: number) => Promise<ProcStat | undefined>;
  private readonly readPssBytes: (pid: number) => Promise<number | undefined>;
  private readonly listProcPids: () => Promise<number[]>;
  private readonly isLinux: () => boolean;

  private readonly vcpuSamples: number[] = [];
  private readonly pssSamples: number[] = [];
  private sampleCount = 0;

  private timer: ReturnType<typeof setInterval> | null = null;
  /** Skip a tick if the prior async pass is still reading. */
  private sampling = false;
  /** Per-leased-child previous CPU-tick reading, for the poll's delta. */
  private readonly lastCpu = new Map<PooledChild<Handle>, { ticks: number; at: number }>();
  /** Consecutive poll passes where leased children yielded no readable group data. */
  private readFailureStreak = 0;
  /** Consecutive poll passes where groups were readable but no member had readable memory. */
  private memReadFailureStreak = 0;

  constructor(options: WorkerPoolResourceSamplerOptions<Handle>) {
    this.children = options.children;
    this.intervalMs = options.intervalMs;
    this.now = options.now;
    this.isDraining = options.isDraining;
    this.logger = options.logger;
    this.readProcStat = options.readProcStat ?? defaultReadProcStat;
    this.readPssBytes = options.readPssBytes ?? defaultReadPssBytes;
    this.listProcPids = options.listProcPids ?? defaultListProcPids;
    this.isLinux = options.isLinux ?? (() => process.platform === 'linux');
  }

  /** Percentiles, or `undefined` until at least one sample lands. */
  stats(): WorkerPoolResourceStats | undefined {
    if (this.sampleCount === 0) return undefined;
    return {
      ...(this.vcpuSamples.length > 0 ? { vcpu: percentiles(this.vcpuSamples) } : {}),
      ...(this.pssSamples.length > 0 ? { pssBytes: percentiles(this.pssSamples) } : {}),
      sampleCount: this.sampleCount,
    };
  }

  /**
   * Arm the leased-session poll on first spawn. `.unref()`'d (never holds the
   * process open); an idle/per-call-only pool's tick is a cheap no-op.
   */
  start(): void {
    if (this.timer !== null || this.intervalMs <= 0 || this.isDraining()) return;
    // `.catch` the pass: poll() runs on an always-on unref'd timer, so an unguarded
    // rejection (e.g. a custom-injected reader that throws) would be an unhandled
    // rejection — capable of taking down the worker for a dropped calibration sample.
    this.timer = setInterval(() => void this.poll().catch(() => {}), this.intervalMs);
    this.timer.unref?.();
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.lastCpu.clear();
  }

  /** Snapshot a single child's CPU+clock at checkout (per-call model). */
  async beginCall(pid: number): Promise<CallSample | undefined> {
    const stat = await this.readProcStat(pid);
    if (stat === undefined) return undefined;
    return { startTicks: stat.cpuTicks, startAt: this.now() };
  }

  /**
   * Redeem a {@link beginCall} snapshot at settle: record the call's vCPU. Calls
   * shorter than {@link MIN_MEASURABLE_CALL_WALL_MS} record nothing — below a few
   * kernel ticks the ratio is quantization noise, not a measurement.
   */
  async endCall(pid: number, begin: CallSample): Promise<void> {
    // Stamp the settle time before the read so the read's own I/O latency isn't
    // billed to the call window (symmetric with beginCall, which stamps after its read).
    const endAt = this.now();
    if (endAt - begin.startAt < MIN_MEASURABLE_CALL_WALL_MS) return;
    const stat = await this.readProcStat(pid);
    const vcpu = this.computeVcpu(stat?.cpuTicks, begin.startTicks, endAt - begin.startAt);
    this.record(vcpu, undefined);
  }

  /**
   * One poll pass: sample each leased child over its process group. Invoked by the
   * internal timer; `public` so tests can drive a pass deterministically.
   */
  async poll(): Promise<void> {
    if (this.sampling || this.isDraining()) return;
    const leased = [...this.children].filter(
      (w) => w.lease && !w.retired && w.child.pid !== undefined
    );
    // Drop remembered readings for children that ended since the last pass. Deleting
    // during Map-key iteration is well-defined (no await between), so no snapshot needed.
    for (const child of this.lastCpu.keys()) {
      if (!leased.includes(child)) this.lastCpu.delete(child);
    }
    if (leased.length === 0) return;
    this.sampling = true;
    try {
      // Resolve each leased child's own process-group id (not assuming pid == pgid:
      // that only holds for a `killProcessGroup` group-leader). Group by pgid so a
      // runner and its still-running Chromium descendants collapse to one total.
      const childPgid = new Map<PooledChild<Handle>, number>();
      for (const child of leased) {
        const pid = child.child.pid;
        if (pid === undefined) continue;
        const stat = await this.readProcStat(pid);
        if (stat) childPgid.set(child, stat.pgrp);
      }
      const targetPgids = new Set(childPgid.values());
      if (targetPgids.size === 0) {
        this.trackReadHealth(false); // leased children, but no pgrp readable
        return;
      }

      const groups = await this.readGroupTotals(targetPgids);
      this.trackReadHealth(groups.size > 0);
      // The memory dimension can fail on its own (e.g. a hardened runtime blocks
      // `smaps_rollup` AND the VmRSS fallback while `stat` stays readable): CPU
      // keeps sampling, `trackReadHealth` keeps passing, and `pssBytes` would just
      // never appear. A live process always has nonzero memory, so "every readable
      // group summed to 0" means the memory reads themselves are failing.
      if (groups.size > 0) {
        this.trackMemoryReadHealth([...groups.values()].some((g) => g.pssBytes > 0));
      }
      const at = this.now();
      for (const child of leased) {
        const pgid = childPgid.get(child);
        if (pgid === undefined) continue;
        const total = groups.get(pgid) ?? { cpuTicks: 0, pssBytes: 0 };
        const prev = this.lastCpu.get(child);
        this.lastCpu.set(child, { ticks: total.cpuTicks, at });
        // First pass has no delta — record PSS now, defer vCPU to the next pass.
        const vcpu = prev ? this.computeVcpu(total.cpuTicks, prev.ticks, at - prev.at) : undefined;
        this.record(vcpu, total.pssBytes > 0 ? total.pssBytes : undefined);
      }
    } finally {
      this.sampling = false;
    }
  }

  /**
   * False-green guard: warn once after {@link READ_FAILURE_WARN_STREAK} consecutive
   * poll passes that had leased children but read no group data — the sampler is
   * armed yet silently collecting nothing (a broken `/proc` on the host). Gated on
   * Linux so the expected off-platform (macOS dev) case stays quiet.
   */
  private trackReadHealth(readable: boolean): void {
    if (readable) {
      this.readFailureStreak = 0;
      return;
    }
    this.readFailureStreak++;
    if (this.isLinux() && this.readFailureStreak === READ_FAILURE_WARN_STREAK) {
      this.logger.warn(
        '[WorkerPool] resource sampler armed but every leased-group read failed — no CPU/PSS samples',
        { consecutivePasses: this.readFailureStreak }
      );
    }
  }

  /**
   * Memory-dimension sibling of {@link trackReadHealth}: warns once when groups
   * keep resolving (CPU sampling healthy) but every member's memory read keeps
   * failing — otherwise a PSS-only outage is invisible until someone notices the
   * `pssBytes` series simply never exists.
   */
  private trackMemoryReadHealth(readable: boolean): void {
    if (readable) {
      this.memReadFailureStreak = 0;
      return;
    }
    this.memReadFailureStreak++;
    if (this.isLinux() && this.memReadFailureStreak === READ_FAILURE_WARN_STREAK) {
      this.logger.warn(
        '[WorkerPool] resource sampler reading CPU but every group memory read failed — no PSS samples',
        { consecutivePasses: this.memReadFailureStreak }
      );
    }
  }

  /**
   * Walk `/proc` once and sum CPU ticks + PSS **for the target process groups only**.
   * Every pid needs a `stat` read to learn its pgrp, but the costlier memory read is
   * skipped unless the pid belongs to a leased group — so the pass is O(host) cheap
   * stat reads + O(leased-group members) `smaps_rollup` reads, not O(host) of both.
   * PSS, not VmRSS, so the sum is the group's true incremental footprint (see the
   * module doc for the shared-page rationale).
   */
  private async readGroupTotals(
    targetPgids: Set<number>
  ): Promise<Map<number, { cpuTicks: number; pssBytes: number }>> {
    const groups = new Map<number, { cpuTicks: number; pssBytes: number }>();
    const pids = await this.listProcPids();
    for (const pid of pids) {
      const stat = await this.readProcStat(pid);
      if (stat === undefined || !targetPgids.has(stat.pgrp)) continue;
      const pss = (await this.readPssBytes(pid)) ?? 0;
      const entry = groups.get(stat.pgrp) ?? { cpuTicks: 0, pssBytes: 0 };
      entry.cpuTicks += stat.cpuTicks;
      entry.pssBytes += pss;
      groups.set(stat.pgrp, entry);
    }
    return groups;
  }

  /** ΔcpuSeconds / ΔwallSeconds, or `undefined` if the window/reading is unusable. */
  private computeVcpu(
    endTicks: number | undefined,
    startTicks: number,
    wallMs: number
  ): number | undefined {
    if (endTicks === undefined || wallMs <= 0) return undefined;
    const deltaTicks = endTicks - startTicks;
    if (deltaTicks < 0) return undefined; // pid reuse / group churn — discard
    const vcpu = deltaTicks / USER_HZ / (wallMs / 1000);
    if (!Number.isFinite(vcpu) || vcpu > MAX_PLAUSIBLE_VCPU) return undefined;
    return vcpu;
  }

  /** Push whichever dimensions are present into the rolling windows. */
  private record(vcpu: number | undefined, pssBytes: number | undefined): void {
    let recorded = false;
    if (vcpu !== undefined && Number.isFinite(vcpu)) {
      push(this.vcpuSamples, vcpu);
      recorded = true;
    }
    if (pssBytes !== undefined && Number.isFinite(pssBytes) && pssBytes > 0) {
      push(this.pssSamples, pssBytes);
      recorded = true;
    }
    if (recorded) this.sampleCount++;
  }
}

/** Append to a bounded rolling window, dropping the oldest past the cap. */
function push(buf: number[], value: number): void {
  buf.push(value);
  if (buf.length > MAX_SAMPLES) buf.shift();
}
